import { useId, useState, type FormEvent } from 'react';
import { ClubPicker } from './ClubPicker';
import { Mail, UserPlus, Building2, Check } from 'lucide-react';
import { requestToJoinClub, startRegistration, verifyRegistration } from '../../lib/api/onboarding';
import type { OnboardingGrantHolder } from '../../lib/auth/onboarding';
import { useT } from '../../i18n';
import { Padel24Logo } from '../ui';
import { formatOtpCodeInput } from './otpCode';
import { useRetryWait } from '../ui/useRetryWait';

// ─────────────────────────────────────────────────────────────────────
// «E-POST — KODE — DU ER INNE» (#710).
//
// The journey a person with no account walks: register, read a code, ask a club
// to let them in. One screen with four steps, because they are one errand and
// four screens would each need their own way back.
//
//   address   → e-mail and name, which is everything registration asks for.
//   code      → the six digits. Same shape as every other code step here.
//   club      → which club to ask. Pre-filled from `?club=…` when the link had
//               one, so the ordinary arrival is one button.
//   done      → it is with the club now, and a human will answer.
//
// ── WHAT THIS SCREEN PROMISES, AND WHAT IT MUST NOT ────────────────
//
// Asking to join is NOT joining. A pending request grants nothing — that is the
// endpoint's whole design — so the last step says a person has to approve it,
// and offers no way into the app. Saying «welcome» here and then refusing every
// read would be the worse failure: the member app has no data source without a
// member session, and one only exists once a club says yes.
//
// ── THE SCREEN KNOWS NO MORE THAN THE SERVER SAYS ──────────────────
//
// `register/start` answers the same 202 for a new address, one that already has
// an account, and one known under another provider. So the code step is reached
// for all three, and the text never claims a code was definitely sent to a
// definitely-new account. A form that said «already registered» would be a
// membership list anybody can read.
//
// ── A RELOAD GOES BACK TO THE BEGINNING, AND SAYS SO ───────────────
//
// The grant lives in memory only (`lib/auth/onboarding.ts`). Losing it costs one
// e-mail, because the registration door is idempotent — so `expired` returns to
// the address step with a sentence explaining why, rather than failing at the
// next call with something about a token.
// ─────────────────────────────────────────────────────────────────────

/** Mirrors `OTP_LIFETIME_MS`, for the sentence about how long a code lives. */
const CODE_LIFETIME_MINUTES = 10;

export interface MemberOnboardingScreenProps {
  /** The club from the link (`?club=<slug>`), when there was one. */
  clubSlug: string | null;
  /** Where the grant is kept. Passed in so the screen owns no credential. */
  grant: OnboardingGrantHolder;
  /** Back to the entrance. */
  onBack: () => void;
}

type Step =
  | { name: 'address'; error: string | null }
  | { name: 'sending' }
  | { name: 'code'; error: string | null }
  | { name: 'verifying' }
  | { name: 'club'; error: string | null }
  | { name: 'asking' }
  | { name: 'done'; club: string };

export function MemberOnboardingScreen({ clubSlug, grant, onBack }: MemberOnboardingScreenProps) {
  const t = useT();
  // One id for the whole journey: the four steps are mutually exclusive, so the
  // message on screen always belongs to the field on screen.
  const errorId = useId();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [club, setClub] = useState(clubSlug ?? '');
  const [step, setStep] = useState<Step>({ name: 'address', error: null });

  // THE ACTUAL WAIT, PER STEP (#778 Q7): the code mail, the code and the join
  // request are three budgets, so a wait on one never holds another.
  const wait = useRetryWait<'send' | 'verify' | 'ask'>();
  const busy = step.name === 'sending' || step.name === 'verifying' || step.name === 'asking';

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || wait.waiting('send')) return;
    wait.release('send');
    setStep({ name: 'sending' });
    const outcome = await startRegistration({ email: email.trim(), name: name.trim() });
    if (outcome.status === 'accepted') {
      // MOVES ON FOR EVERY ACCEPTED REQUEST, including an address that already
      // has an account — the server cannot tell us, and a screen that stayed put
      // would answer the question the API refuses to.
      setStep({ name: 'code', error: null });
      return;
    }
    if (outcome.status === 'invalid') {
      setStep({
        name: 'address',
        error:
          outcome.field === 'name'
            ? t('onboarding.register.nameRequired')
            : t('onboarding.register.invalidEmail'),
      });
      return;
    }
    if (outcome.status === 'throttled') {
      wait.start('send', outcome.retryAfterSeconds);
      setStep({ name: 'address', error: null });
      return;
    }
    if (outcome.status === 'not_configured') {
      setStep({ name: 'address', error: t('errors.not_configured') });
      return;
    }
    setStep({ name: 'address', error: t('onboarding.network') });
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || wait.waiting('verify')) return;
    wait.release('verify');
    setStep({ name: 'verifying' });
    const outcome = await verifyRegistration({ email: email.trim(), code: code.trim() });
    if (outcome.status === 'registered') {
      grant.hold({
        grant: outcome.grant,
        expiresAt: outcome.expiresAt,
        player: outcome.player,
      });
      setStep({ name: 'club', error: null });
      return;
    }
    if (outcome.status === 'throttled') {
      wait.start('verify', outcome.retryAfterSeconds);
      setStep({ name: 'code', error: null });
      return;
    }
    if (outcome.status === 'not_configured') {
      setStep({ name: 'code', error: t('errors.not_configured') });
      return;
    }
    if (outcome.status === 'unreachable') {
      setStep({ name: 'code', error: t('onboarding.network') });
      return;
    }
    if (outcome.status === 'protocol') {
      // NOT «wrong code»: the code may have been perfect and the answer broken.
      setStep({ name: 'code', error: t('onboarding.protocol') });
      return;
    }
    // ONE MESSAGE FOR EVERY REFUSAL — see the header.
    setStep({ name: 'code', error: t('onboarding.code.refused') });
  };

  const ask = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || wait.waiting('ask')) return;
    const token = grant.token();
    if (!token) {
      // THE GRANT IS GONE — a reload, or thirty minutes. Back to the start with
      // a reason, because «ask again» is the fix and it costs one e-mail.
      setStep({ name: 'address', error: t('onboarding.grantExpired') });
      return;
    }
    wait.release('ask');
    setStep({ name: 'asking' });
    const outcome = await requestToJoinClub({ clubSlug: club.trim().toLowerCase(), grant: token });

    if (outcome.status === 'pending') {
      grant.release();
      setStep({ name: 'done', club: outcome.club.name });
      return;
    }
    const message = (): string => {
      switch (outcome.status) {
        case 'already_member':
          return t('onboarding.join.alreadyMember');
        case 'unknown_club':
          return t('onboarding.join.unknownClub');
        case 'blocked':
          return t('onboarding.join.blocked');
        case 'not_yet':
          return t('onboarding.join.notYet');
        case 'closed':
          return t('onboarding.join.closed');
        case 'too_many':
          // A standing cap: it does not clear with time, so there is no wait.
          return t('onboarding.join.tooManyPending');
        case 'protocol':
          // THE ONE SENTENCE THIS JOURNEY MUST NEVER SAY FALSELY is «søknaden
          // er sendt». A 2xx we cannot read does not earn it (#733 review).
          return t('onboarding.protocol');
        default:
          return t('onboarding.network');
      }
    };
    if (outcome.status === 'expired') {
      grant.release();
      setStep({ name: 'address', error: t('onboarding.grantExpired') });
      return;
    }
    if (outcome.status === 'throttled') {
      wait.start('ask', outcome.retryAfterSeconds);
      setStep({ name: 'club', error: null });
      return;
    }
    setStep({ name: 'club', error: message() });
  };

  // A refusal's own sentence, or else the countdown of a wait still running.
  const error =
    (step.name === 'address' || step.name === 'code' || step.name === 'club' ? step.error : null) ??
    wait.notice;
  const who = grant.state()?.player.name ?? null;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#6b21a8' }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Padel24Logo size="xl" variant="full" tone="light" />
          </div>
          {/* ON THE PURPLE, NOT ON A CARD. The ink scale is chosen for
              `--paper`; `text-ink-3` on this `#6b21a8` is 2.78:1, under the
              4.5:1 floor (#781). 80% white is 6.11:1. Found across six screens
              at once when the contrast guard stopped being a hand-written list
              of two (#752). */}
          <p className="text-sm text-white/80">{t('onboarding.tagline')}</p>
        </div>

        <div className="card p-6">
          {(step.name === 'address' || step.name === 'sending') && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <UserPlus className="w-5 h-5 text-ink-3" aria-hidden="true" />
                <h2 className="font-display text-lg font-semibold">
                  {t('onboarding.register.title')}
                </h2>
              </div>
              <p className="text-xs text-ink-3 mb-4">{t('onboarding.register.lead')}</p>
              <form onSubmit={send} className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                    {t('onboarding.register.nameLabel')}
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    className="input w-full"
                    disabled={busy}
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                    {t('onboarding.register.emailLabel')}
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      // Another address is another mail budget.
                      wait.release('send');
                    }}
                    autoComplete="email"
                    inputMode="email"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    className="input w-full"
                    disabled={busy}
                  />
                </label>
                {error && (
                  <p id={errorId} role="alert" className="text-xs text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={busy || wait.waiting('send')}
                >
                  {busy ? t('onboarding.register.sending') : t('onboarding.register.submit')}
                </button>
              </form>
              <button
                type="button"
                onClick={onBack}
                className="mt-4 text-sm text-ink-3 underline w-full"
              >
                {t('onboarding.haveCode')}
              </button>
            </>
          )}

          {(step.name === 'code' || step.name === 'verifying') && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-5 h-5 text-ink-3" aria-hidden="true" />
                <h2 className="font-display text-lg font-semibold">{t('onboarding.code.title')}</h2>
              </div>
              {/* The address is echoed because a typo is the likeliest reason no
                  code arrives, and it is the one thing the person can check. */}
              <p className="text-xs text-ink-3 mb-4">
                {t('onboarding.code.lead', { email: email.trim(), minutes: CODE_LIFETIME_MINUTES })}
              </p>
              <form onSubmit={verify} className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                    {t('onboarding.code.label')}
                  </span>
                  <input
                    type="text"
                    value={code}
                    // NORMALISED, NOT TRUNCATED (see `otpCode.ts`). `maxLength`
                    // is gone on purpose: it cut a pasted «123 456» to six
                    // characters before this handler ever ran, and the cap now
                    // lives after the non-digits are stripped.
                    onChange={(e) => setCode(formatOtpCodeInput(e.target.value))}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    className="input w-full text-center tracking-[0.3em] text-lg"
                    disabled={busy}
                  />
                </label>
                {error && (
                  <p id={errorId} role="alert" className="text-xs text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={busy || wait.waiting('verify')}
                >
                  {busy ? t('onboarding.code.verifying') : t('onboarding.code.submit')}
                </button>
              </form>
              <button
                type="button"
                onClick={() => {
                  // A different address starts with its own budgets.
                  wait.clear();
                  setStep({ name: 'address', error: null });
                }}
                className="mt-4 text-sm text-ink-3 underline w-full"
                disabled={busy}
              >
                {t('onboarding.code.wrongAddress')}
              </button>
            </>
          )}

          {(step.name === 'club' || step.name === 'asking') && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-5 h-5 text-ink-3" aria-hidden="true" />
                <h2 className="font-display text-lg font-semibold">{t('onboarding.join.title')}</h2>
              </div>
              <p className="text-xs text-ink-3 mb-4">
                {who ? t('onboarding.join.leadNamed', { name: who }) : t('onboarding.join.lead')}
              </p>
              <form onSubmit={ask} className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
                    {t('onboarding.join.clubLabel')}
                  </span>
                  {/* THE FIELD THAT STARTED #742. «Smaalenene Padelklubb»
                      was refused and the slug had to be guessed. */}
                  <ClubPicker value={club} onChange={setClub} disabled={busy} />
                </label>
                {/* ANNOUNCED, BUT NOT ASSOCIATED. `ClubPicker` owns its own
                    input, so `aria-describedby` cannot be put on the field from
                    here without giving the picker a prop for it. `role="alert"`
                    is what makes the refusal reach a screen reader at all. */}
                {error && (
                  <p role="alert" className="text-xs text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  className="btn btn-primary w-full"
                  disabled={busy || club.trim() === '' || wait.waiting('ask')}
                >
                  {busy ? t('onboarding.join.asking') : t('onboarding.join.submit')}
                </button>
              </form>
            </>
          )}

          {step.name === 'done' && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Check className="w-5 h-5 text-green-600" aria-hidden="true" />
                <h2 className="font-display text-lg font-semibold">{t('onboarding.done.title')}</h2>
              </div>
              {/* NO WAY INTO THE APP FROM HERE, deliberately: a pending request
                  grants nothing, and the member app has no data source without a
                  member session. Promising entry and then refusing every read is
                  the worse failure. */}
              <p className="text-xs text-ink-3 mb-4">
                {t('onboarding.done.lead', { club: step.club })}
              </p>
              <p className="text-xs text-ink-3 mb-4">{t('onboarding.done.next')}</p>
              <button type="button" onClick={onBack} className="btn-ghost w-full justify-center">
                {t('onboarding.done.back')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
